import { InvoicingLifecycleCommand, invoiceLifecycle, resolveFinancialDocumentTransition } from '../financial/financial.document.lifecycle';
import { InvoiceStatus } from './invoicing.enum';

export interface InvoiceCommandDecision {
  ok: boolean;
  /** Repeat call whose effect already happened — return the current record. */
  idempotent?: boolean;
  reason?: string;
  to?: InvoiceStatus;
}

export const VOID_WITH_PAYMENT_REASON = 'Cannot void: a payment has been applied.';

type LifecycleDocument = { status?: InvoiceStatus; hasPayments?: boolean; amountPaid?: number };

/**
 * A payment pins an invoice or bill: voiding it leaves the money recorded against a void record.
 * Screens hide Void on this rule, and the server refuses on it, because a screen opened before the
 * payment still offers Void (WBS-12196).
 */
export function hasPaymentApplied(document: Pick<LifecycleDocument, 'hasPayments' | 'amountPaid'> | null | undefined): boolean {

  return document?.hasPayments === true || Number(document?.amountPaid ?? 0) > 0;
}

export function decideInvoiceCommand(invoice: LifecycleDocument | null | undefined, command: InvoicingLifecycleCommand): InvoiceCommandDecision {

  if (!invoice) return { ok: false, reason: 'Invoice not found' };

  if (isInvoiceCommandIdempotentNoOp(invoice, command)) {
    return { ok: true, idempotent: true, to: invoice.status };
  }

  if (command === InvoicingLifecycleCommand.Void && hasPaymentApplied(invoice)) {
    return { ok: false, reason: VOID_WITH_PAYMENT_REASON };
  }

  const transition = resolveFinancialDocumentTransition(invoiceLifecycle, invoice.status, command);
  if (!transition) {
    return { ok: false, reason: `${command} is not allowed from ${invoice.status}` };
  }

  return { ok: true, to: transition.to };
}

export function isInvoiceCommandIdempotentNoOp(invoice: LifecycleDocument | null | undefined, command: InvoicingLifecycleCommand): boolean {

  if (!invoice?.status) return false;

  switch (command) {
    case InvoicingLifecycleCommand.Approve:
    case InvoicingLifecycleCommand.Retry:
      return invoice.status === InvoiceStatus.Approved;
    case InvoicingLifecycleCommand.Void:
      return invoice.status === InvoiceStatus.Void;
    case InvoicingLifecycleCommand.Reissue:
    case InvoicingLifecycleCommand.Sync:
      return false;
    default:
      return false;
  }
}
